import LabInputRow from '../../controllers/LabInputRow'
import TemplateData from '../../controllers/TemplateData'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import LimeProgramLabInputRow from '../../controllers/LimeProgramLabInputRow'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import { ELEMENT_SPECIFIC_GRAVITY, BASE_SATURATION_BY_CEC_STATIC, BASE_SATURATION_BY_DENSITY_STATIC, ELEMENTAL_SULPHUR_BY_TEXTURE } from './staticdata'

// Re-export LimePlayFieldRow as LabInfoRow for backward compatibility
export type LabInfoRow = LimePlayFieldRow

// Lime type enum
export type LimeType = 'Calcitic' | 'Dolomitic'

// Default product values (from Excel - can be overridden by template)
export const DEFAULT_PRODUCT_VALUES = {
    calcitic: {
        caPercent: 0.34,      // 34% Ca in calcitic lime
        mgPercent: 0.003,     // 0.3% Mg in calcitic lime
        cce: 0.82             // 82% CCE (purity)
    },
    dolomitic: {
        caPercent: 0.236,     // 23.6% Ca in dolomitic lime
        mgPercent: 0.111,     // 11.1% Mg in dolomitic lime
        cce: 0.95             // 95% CCE (purity)
    },
    gypsum: {
        caPercent: 0.2326,    // 23.26% Ca in gypsum
        sPercent: 0.18,       // 18% S in gypsum
        cce: 0.95             // 95% CCE
    }
}

// Element type for specific gravity lookup
type Element = keyof typeof ELEMENT_SPECIFIC_GRAVITY;

/**
 * Convert element from mg/kg to cmolc/kg
 * Formula: value (mg/kg) / specific gravity
 */
export function mgKgToCmolc(value: number, element: Element): number {
    return value / ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Convert mg/kg to kg/ha at specified depth
 * Formula: value (mg/kg) * density * (depthCm / 10)
 */
export function mgKgToKgHa(value: number, density: number, depthCm: number): number {
    return value * density * (depthCm / 10)
}

/**
 * Calculate ratio between two values (safe division)
 * Returns 0 if divisor is 0
 */
export function ratio(numerator: number, divisor: number): number {
    if (divisor === 0) return 0
    return numerator / divisor
}

/**
 * Lookup CEC operator from CEC value using VLOOKUP-style lookup
 * Excel: =VLOOKUP(V19,Parameters!$E$47:$F$59,2,TRUE)
 * Finds the largest operatingCec value that is <= the input cec
 */
export function lookupCecOperator(cec: number): number {
    let result = BASE_SATURATION_BY_CEC_STATIC[0]!
    for (const entry of BASE_SATURATION_BY_CEC_STATIC) {
        if (entry.operatingCec <= cec) {
            result = entry
        } else {
            break
        }
    }
    return result.operator
}

/**
 * Lookup Density operator from density value using VLOOKUP-style lookup
 * Excel: =VLOOKUP(K19,Parameters!$E$35:$F$43,2,TRUE)
 * Finds the largest operatingDensity value that is <= the input density
 */
export function lookupDensityOperator(density: number): number {
    let result = 1 // Default operator
    for (const entry of BASE_SATURATION_BY_DENSITY_STATIC) {
        if (entry.operatingDensity <= density) {
            result = BASE_SATURATION_BY_DENSITY_STATIC.indexOf(entry) + 1
        } else {
            break
        }
    }
    return result
}

/**
 * Get base saturation norms by CEC operator
 * Returns the norm percentages (Ca%, Mg%, K%, Ca:Mg ratio) for a given CEC operator
 */
export function getBaseSaturationNormsByCec(cecOperator: number) {
    const entry = BASE_SATURATION_BY_CEC_STATIC.find(e => e.operator === cecOperator)
    // Always return a valid entry - default to first entry if not found
    return entry ?? BASE_SATURATION_BY_CEC_STATIC[0]!
}

/**
 * Calculate nutrient level at norm (mg/kg)
 * Formula: normPercent * CEC * specific_gravity
 * Excel: =$AF19*V19*SGCa (e.g., Ca norm% * CEC * 200)
 */
export function calcNutrientLevelAtNorm(normPercent: number, cec: number, element: Element): number {
    return normPercent * cec * ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Lookup depth multiplier factor from density operator and depth
 * Excel: =VLOOKUP(AE19,Parameters!$F$35:$Q$43,VLOOKUP($AV$8,Parameters!$AV$21:$AW$28,2,FALSE),FALSE)
 */
export function lookupDepthFactor(densityOperator: number, depthCm: number): number {
    const entry = BASE_SATURATION_BY_DENSITY_STATIC[densityOperator - 1]
    if (!entry) return 3.3 // Default for 30cm

    const depthKey = `depth${depthCm}` as keyof typeof entry.depthMultipliers
    return entry.depthMultipliers[depthKey] ?? 3.3
}

/**
 * Determine lime type based on Ca and Mg deficits and dolomitic rules
 * Excel logic: If Ca deficit > Mg deficit AND not meeting Mg threshold, use Calcitic, else Dolomitic
 */
export function determineLimeType(
    caDiffMgKg: number,
    mgDiffMgKg: number,
    cec: number,
    mgMgKg: number,
    template: TemplateData
): LimeType {
    // Get the dolomitic rule for this CEC level
    const dolomiticRules = template.lime.dolomiticRules

    // Find the appropriate CEC threshold
    let mgThreshold = 0
    const cecKeys = Object.keys(dolomiticRules) as Array<keyof typeof dolomiticRules>
    for (const key of cecKeys) {
        const rule = dolomiticRules[key]
        if (rule.cec <= cec) {
            mgThreshold = rule.mgThresholdPpm
        }
    }

    // If Ca deficit > Mg deficit AND Mg is above threshold, use Calcitic
    if (caDiffMgKg < mgDiffMgKg && mgMgKg >= mgThreshold) {
        return 'Calcitic'
    }
    return 'Dolomitic'
}

/**
 * Determine if lime is needed (Lime Rule)
 * Excel: Complex IF logic checking various conditions
 */
export function calcLimeRule(
    caPercent: number,
    caPercentNorm: number,
    mgMgKg: number,
    caMgKg: number,
    exchH: number,
    template: TemplateData
): boolean {
    // Check if we need to reach ideal Mg
    if (template.lime.reachIdealMg) {
        return mgMgKg < template.lime.idealMgMgKg
    }

    // Check if we need to reach ideal Ca
    if (template.lime.reachIdealCa) {
        return caMgKg < template.lime.idealCaMgKg
    }

    // Check if current Ca% is below norm
    if (caPercent < caPercentNorm) {
        return true
    }

    // Check for acid neutralization
    if (template.lime.neutraliseAcid && exchH > 0) {
        return true
    }

    return false
}

/**
 * Calculate Ca amendment supply (kg/ha)
 * Formula based on Ca deficit, lime type, and depth factor
 */
export function calcCaAmendmentSupply(
    caDiffMgKg: number,
    depthFactor: number,
    limeType: LimeType,
    products = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Calcitic' ? products.calcitic : products.dolomitic
    const caPercent = product.caPercent
    const cce = product.cce

    if (caDiffMgKg >= 0) return 0 // No deficit, no amendment needed

    // Convert deficit to kg/ha and divide by product Ca content and purity
    const amountNeeded = Math.abs(caDiffMgKg) * depthFactor / caPercent / cce
    return Math.max(0, amountNeeded)
}

/**
 * Calculate Mg amendment supply (kg/ha)
 * Formula based on Mg deficit, lime type, and depth factor
 */
export function calcMgAmendmentSupply(
    mgDiffMgKg: number,
    depthFactor: number,
    limeType: LimeType,
    products = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Calcitic' ? products.calcitic : products.dolomitic
    const mgPercent = product.mgPercent
    const cce = product.cce

    if (mgDiffMgKg >= 0 || mgPercent === 0) return 0 // No deficit or no Mg in product

    // Convert deficit to kg/ha and divide by product Mg content and purity
    const amountNeeded = Math.abs(mgDiffMgKg) * depthFactor / mgPercent / cce
    return Math.max(0, amountNeeded)
}

/**
 * Calculate Mg supply for ideal Mg target (kg/ha)
 * Excel: =IF(H19="","",IF(Parameters!$Y$14="Yes",((Parameters!$Y$15-H19)/$AX$3*AQ19),0))
 */
export function calcMgIdealSupply(
    mgMgKg: number,
    idealMgMgKg: number,
    depthFactor: number,
    reachIdealMg: boolean,
    products = DEFAULT_PRODUCT_VALUES
): number {
    if (!reachIdealMg) return 0

    const deficit = idealMgMgKg - mgMgKg
    if (deficit <= 0) return 0

    return deficit / products.dolomitic.mgPercent * depthFactor
}

/**
 * Calculate Ca supply for ideal Ca target (kg/ha)
 * Excel: =IF(G19="","",IF(Parameters!$Y$17="Yes",(Parameters!$Y$18-G19)*AQ19/IF(AT19="Calcitic lime",BronPersCa,BronPersMg),0))
 */
export function calcCaIdealSupply(
    caMgKg: number,
    idealCaMgKg: number,
    depthFactor: number,
    reachIdealCa: boolean,
    limeType: LimeType,
    products = DEFAULT_PRODUCT_VALUES
): number {
    if (!reachIdealCa) return 0

    const deficit = idealCaMgKg - caMgKg
    if (deficit <= 0) return 0

    const product = limeType === 'Calcitic' ? products.calcitic : products.dolomitic
    return deficit * depthFactor / product.caPercent
}

/**
 * Calculate amendment need (maximum of various calculations)
 * Excel: =MAX(AU19, AV19, AW19, AX19, AY19, AZ19)
 */
export function calcAmendmentNeed(
    caSupply: number,
    mgSupply: number,
    mgIdealSupply: number,
    caIdealSupply: number
): number {
    return Math.max(caSupply, mgSupply, mgIdealSupply, caIdealSupply, 0)
}

/**
 * Get max lime application based on CEC
 * Excel: VLOOKUP(BD19, Parameters!$AC$13:$AD$19,2,TRUE)
 */
export function getMaxLimeApplication(cec: number, template: TemplateData): number {
    if (!template.lime.maxBreakActivated) return Infinity

    const rules = template.lime.maxLimeRules
    const cecRules = [
        rules.cec1, rules.cec2, rules.cec3, rules.cec4,
        rules.cec6, rules.cec8, rules.cec10
    ].sort((a, b) => a.cec - b.cec)

    let maxKg = cecRules[0]?.maxKg ?? 8000
    for (const rule of cecRules) {
        if (rule.cec <= cec) {
            maxKg = rule.maxKg
        }
    }
    return maxKg
}

/**
 * Calculate applied year 1 (kg/ha)
 * Excel: Complex formula limiting to max application and min threshold
 */
export function calcAppliedYear1(
    amendmentNeed: number,
    maxApplication: number,
    minRate: number,
    minBreakActivated: boolean,
    phKcl: number,
    phWarningThreshold: number,
    applyBrakes: boolean
): number {
    // Check if pH is already at warning threshold
    if (applyBrakes && phKcl >= phWarningThreshold) {
        return 0
    }

    // Check minimum rate threshold
    if (minBreakActivated && amendmentNeed < minRate) {
        return 0
    }

    // Apply max application limit
    return Math.min(amendmentNeed, maxApplication)
}

/**
 * Calculate applied year 2 (kg/ha)
 * Excel: Remaining after year 1, limited by max application
 */
export function calcAppliedYear2(
    amendmentNeed: number,
    appliedYear1: number,
    maxApplication: number,
    minRate: number
): number {
    if (appliedYear1 <= 0) return 0

    const remaining = amendmentNeed - appliedYear1
    if (remaining < minRate) return 0

    return Math.min(remaining, maxApplication)
}

/**
 * Calculate applied year 3 (kg/ha)
 * Excel: Remaining after year 1+2, limited by max application
 */
export function calcAppliedYear3(
    amendmentNeed: number,
    appliedYear1: number,
    appliedYear2: number,
    maxApplication: number,
    minRate: number
): number {
    if (appliedYear2 <= 0) return 0

    const remaining = amendmentNeed - appliedYear1 - appliedYear2
    if (remaining < minRate) return 0

    return Math.min(remaining, maxApplication)
}

/**
 * Check if gypsum is meaningful (Gypsum Rules)
 * Excel: =IF(BL19="","",(IF(AND(BL19>=Parameters!$W$34,AA19>Parameters!$V$34),"YES","NO")))
 */
export function calcGypsumMeaningful(
    gypsumNeed: number,
    naPercent: number,
    minApplicationKgHa: number,
    minNaPercentage: number
): boolean {
    if (gypsumNeed < minApplicationKgHa) return false
    if (naPercent <= minNaPercentage) return false
    return true
}

/**
 * Calculate gypsum need (kg/ha)
 * Excel: =IFERROR(IF(AA19-Parameters!$V$35<0,0,(((((AA19-Parameters!$V$35)*V19)*SGCa)*AQ19)/$AW$4)),"")
 */
export function calcGypsumNeed(
    naPercent: number,
    finalNaLeachingLevel: number,
    cec: number,
    depthFactor: number,
    products = DEFAULT_PRODUCT_VALUES
): number {
    const naDeficit = naPercent - finalNaLeachingLevel
    if (naDeficit <= 0) return 0

    // Na that needs to be displaced, converted to Ca equivalent
    const caNeed = naDeficit * cec * ELEMENT_SPECIFIC_GRAVITY.Ca

    return caNeed * depthFactor / products.gypsum.caPercent
}

/**
 * Calculate K need (kg/ha)
 * Excel: =IFERROR(IF(AO19/$AW$6*BX19<0,0,AO19/$AW$6*BX19),"")
 */
export function calcKNeed(
    kDiffMgKg: number,
    depthFactor: number,
    kProductPercent: number = 0.4  // Default K2SO4 is 40% K
): number {
    if (kDiffMgKg >= 0) return 0 // No deficit

    const need = Math.abs(kDiffMgKg) / kProductPercent * depthFactor
    return Math.max(0, need)
}

/**
 * Calculate K fertiliser applied year 1 (kg/ha)
 * Based on K need with max application limits
 */
export function calcKAppliedYear1(
    kNeedKgHa: number,
    maxKgHa: number = 500,
    splitOver2Years: boolean = false
): number {
    if (kNeedKgHa <= 0) return 0
    if (splitOver2Years && kNeedKgHa > maxKgHa) {
        return maxKgHa
    }
    return Math.min(kNeedKgHa, maxKgHa)
}

/**
 * Calculate K fertiliser applied year 2 (kg/ha)
 */
export function calcKAppliedYear2(
    kNeedKgHa: number,
    appliedYear1: number,
    maxKgHa: number = 500,
    splitOver2Years: boolean = false
): number {
    if (!splitOver2Years) return 0
    const remaining = kNeedKgHa - appliedYear1
    if (remaining <= 0) return 0
    return Math.min(remaining, maxKgHa)
}

/**
 * Calculate P fertiliser need (kg/ha)
 * Excel: =IFERROR(IF(Parameters!$P$25="YES",(IF((Parameters!$P$26-D19)/$AW$7*BX19<0,0,Parameters!$P$26-D19)/$AW$7*BX19),0),"")
 */
export function calcPNeed(
    pBrayI: number,
    idealP: number,
    depthFactor: number,
    pProductPercent: number = 0.22  // MAP is 22% P
): number {
    const deficit = idealP - pBrayI
    if (deficit <= 0) return 0
    return deficit / pProductPercent * depthFactor
}

/**
 * Calculate P fertiliser applied year 1 (kg/ha)
 */
export function calcPAppliedYear1(
    pNeedKgHa: number,
    minKgHa: number = 50,
    maxKgHa: number = 500
): number {
    if (pNeedKgHa <= 0) return 0
    if (pNeedKgHa < minKgHa) return minKgHa
    return Math.min(pNeedKgHa, maxKgHa)
}

/**
 * Calculate P fertiliser applied year 2 (kg/ha)
 */
export function calcPAppliedYear2(
    pNeedKgHa: number,
    appliedYear1: number,
    maxKgHa: number = 500,
    splitOver2Years: boolean = false
): number {
    if (!splitOver2Years) return 0
    const remaining = pNeedKgHa - appliedYear1
    if (remaining <= 0) return 0
    return Math.min(remaining, maxKgHa)
}

/**
 * Get pH operator for elemental sulphur lookup
 * Excel: =VLOOKUP(V19,Parameters!$M$12:$N$18,2,TRUE)
 */
export function lookupPhOperator(cec: number): number {
    // Simplified lookup - maps CEC ranges to operators 1-7
    if (cec < 6) return 1
    if (cec < 9) return 2
    if (cec < 12) return 3
    if (cec < 18) return 4
    if (cec < 24) return 5
    if (cec < 30) return 6
    return 7
}

/**
 * Calculate elemental sulphur need (kg/ha)
 * Only applies when pH > 7
 * Excel: =IF(CM19="","",IF(C19<7,0,VLOOKUP(...)))
 */
export function calcElementalSulphurNeed(
    phKcl: number,
    phOperator: number,
    targetPh: number = 7.0
): number {
    if (phKcl < 7) return 0

    // Lookup from ELEMENTAL_SULPHUR_BY_TEXTURE based on pH range
    const entry = ELEMENTAL_SULPHUR_BY_TEXTURE.find(e => e.operator === phOperator)
    if (!entry) return 0

    if (phKcl >= 8.5) return entry.toPh85
    if (phKcl >= 8.0) return entry.toPh80
    if (phKcl >= 7.5) return entry.toPh75
    if (phKcl >= 7.0) return entry.toPh70
    return 0
}

/**
 * Calculate Nutricast need based on K (kg/ha)
 * Excel: =IFERROR(IF((Parameters!$AG$65-E19)/'Amendment Data'!$C$78*CO19<0,0,...),"")
 */
export function calcNutricastNeedK(
    kMgKg: number,
    idealKMgKg: number,
    depthFactor: number,
    nutricastKPercent: number = 0.05  // 5% K in Nutricast
): number {
    const deficit = idealKMgKg - kMgKg
    if (deficit <= 0) return 0
    return deficit / nutricastKPercent * depthFactor
}

/**
 * Calculate Nutricast need based on P (kg/ha)
 */
export function calcNutricastNeedP(
    pBrayI: number,
    idealPMgKg: number,
    depthFactor: number,
    nutricastPPercent: number = 0.02  // 2% P in Nutricast
): number {
    const deficit = idealPMgKg - pBrayI
    if (deficit <= 0) return 0
    return deficit / nutricastPPercent * depthFactor
}

/**
 * Calculate Nutricast applied year 1 (kg/ha)
 */
export function calcNutricastAppliedYear1(
    nutricastNeedKgHa: number,
    maxKgHa: number = 1000,
    isEnabled: boolean = false
): number {
    if (!isEnabled || nutricastNeedKgHa <= 0) return 0
    return Math.min(nutricastNeedKgHa, maxKgHa)
}

/**
 * Calculate nutrient inputs from lime (mg/kg added)
 * Excel: Ca input = ((IF(AT19="Dolomitic lime",((BF19/$AZ$3)*$AW$3),((BF19*$AZ$2)*$AW$2))+...) / AQ19
 */
export function calcCaInputFromLime(
    appliedYear1KgHa: number,
    limeType: LimeType,
    depthFactor: number,
    products = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Dolomitic' ? products.dolomitic : products.calcitic
    const caCorrected = appliedYear1KgHa * product.caPercent * product.cce
    return caCorrected / depthFactor
}

/**
 * Calculate Mg input from lime (mg/kg added)
 */
export function calcMgInputFromLime(
    appliedYear1KgHa: number,
    limeType: LimeType,
    depthFactor: number,
    products = DEFAULT_PRODUCT_VALUES
): number {
    const product = limeType === 'Dolomitic' ? products.dolomitic : products.calcitic
    const mgCorrected = appliedYear1KgHa * product.mgPercent * product.cce
    return mgCorrected / depthFactor
}

/**
 * Calculate K input from fertiliser (mg/kg added)
 */
export function calcKInputFromFert(
    kAppliedYear1KgHa: number,
    depthFactor: number,
    kProductPercent: number = 0.4
): number {
    return (kAppliedYear1KgHa * kProductPercent) / depthFactor
}

/**
 * Calculate P input from fertiliser (mg/kg added)
 */
export function calcPInputFromFert(
    pAppliedYear1KgHa: number,
    depthFactor: number,
    pProductPercent: number = 0.22
): number {
    return (pAppliedYear1KgHa * pProductPercent) / depthFactor
}

/**
 * Calculate new nutrient level after amendments (mg/kg)
 */
export function calcNewNutrientLevel(
    currentMgKg: number,
    inputMgKg: number
): number {
    return currentMgKg + inputMgKg
}

/**
 * Calculate new saturation percentage after amendments
 * Formula: (newMgKg / specificGravity) / newCec
 */
export function calcNewSaturationPercent(
    newMgKg: number,
    element: Element,
    newCec: number
): number {
    if (newCec === 0) return 0
    return (newMgKg / ELEMENT_SPECIFIC_GRAVITY[element]) / newCec
}

/**
 * Calculate new CEC (T-value) after amendments
 * Formula: Sum of all cations in cmolc/kg
 */
export function calcNewCec(
    newCaMgKg: number,
    newMgMgKg: number,
    newKMgKg: number,
    newNaMgKg: number
): number {
    return newCaMgKg / ELEMENT_SPECIFIC_GRAVITY.Ca +
           newMgMgKg / ELEMENT_SPECIFIC_GRAVITY.Mg +
           newKMgKg / ELEMENT_SPECIFIC_GRAVITY.K +
           newNaMgKg / ELEMENT_SPECIFIC_GRAVITY.Na
}

/**
 * Calculate new Ca:Mg ratio after amendments
 */
export function calcNewCaMgRatio(
    newCaPercent: number,
    newMgPercent: number
): number {
    if (newMgPercent === 0) return 0
    return newCaPercent / newMgPercent
}

/**
 * Calculate all derived values for a lab input row
 */
export function calculateLabInfoRow(row: LabInputRow, template: TemplateData, depthCm?: number): LabInfoRow {
    const effectiveDepth = depthCm ?? template.lime.depthCm
    const kPercent = row.kPercent / 100
    const naPercent = row.naPercent / 100
    const caPercent = row.caPercent / 100
    const mgPercent = row.mgPercent / 100

    // Lookup operators
    const cecOperator = lookupCecOperator(row.cec)
    const densityOperator = lookupDensityOperator(row.density)

    // Get base saturation norms by CEC operator
    const norms = getBaseSaturationNormsByCec(cecOperator)

    // Calculate nutrient levels at norms (mg/kg)
    const caNormMgKg = calcNutrientLevelAtNorm(norms.ca, row.cec, 'Ca')
    const mgNormMgKg = calcNutrientLevelAtNorm(norms.mg, row.cec, 'Mg')
    const kNormMgKg = calcNutrientLevelAtNorm(norms.k, row.cec, 'K')

    // Calculate nutrient differences
    const caDiffMgKg = row.ca - caNormMgKg
    const mgDiffMgKg = row.mg - mgNormMgKg
    const kDiffMgKg = row.k - kNormMgKg

    // Depth factor for conversions
    const depthFactor = lookupDepthFactor(densityOperator, effectiveDepth)

    // Determine lime type based on deficits
    const limeType = determineLimeType(caDiffMgKg, mgDiffMgKg, row.cec, row.mg, template)

    // Calculate lime rule (is lime needed?)
    const limeRule = calcLimeRule(caPercent, norms.ca, row.mg, row.ca, row.exchH, template)

    // Calculate Ca/Mg amendment supply
    const caSupplyKgHa = calcCaAmendmentSupply(caDiffMgKg, depthFactor, limeType)
    const mgSupplyKgHa = calcMgAmendmentSupply(mgDiffMgKg, depthFactor, limeType)

    // Calculate ideal supplies
    const mgIdealSupply = calcMgIdealSupply(row.mg, template.lime.idealMgMgKg, depthFactor, template.lime.reachIdealMg)
    const caIdealSupply = calcCaIdealSupply(row.ca, template.lime.idealCaMgKg, depthFactor, template.lime.reachIdealCa, limeType)

    // Total amendment need
    const amendmentNeedKgHa = calcAmendmentNeed(caSupplyKgHa, mgSupplyKgHa, mgIdealSupply, caIdealSupply)

    // Max application and yearly calculations
    const maxApplication = getMaxLimeApplication(row.cec, template)
    const appliedYear1KgHa = calcAppliedYear1(
        amendmentNeedKgHa,
        maxApplication,
        template.lime.minRateKgHa,
        template.lime.minBreakActivated,
        row.phH2O,
        template.lime.phWarningThreshold,
        template.lime.applyBrakes
    )
    const appliedYear2KgHa = calcAppliedYear2(amendmentNeedKgHa, appliedYear1KgHa, maxApplication, template.lime.minRateKgHa)
    const appliedYear3KgHa = calcAppliedYear3(amendmentNeedKgHa, appliedYear1KgHa, appliedYear2KgHa, maxApplication, template.lime.minRateKgHa)

    // Gypsum calculations
    const gypsumDepthFactor = lookupDepthFactor(densityOperator, template.gypsum.depthCm)
    const gypsumNeedKgHa = calcGypsumNeed(naPercent, template.gypsum.finalNaLeachingLevel, row.cec, gypsumDepthFactor)
    const gypsumMeaningful = calcGypsumMeaningful(gypsumNeedKgHa, naPercent, template.gypsum.minApplicationKgHa, template.gypsum.minNaPercentage)

    // Gypsum yearly application (simplified - apply in 2 years max)
    const gypsumMaxPerYear = 4000 // Default max per year
    const gypsumYear1KgHa = gypsumMeaningful ? Math.min(gypsumNeedKgHa, gypsumMaxPerYear) : 0
    const gypsumYear2KgHa = gypsumMeaningful ? Math.min(Math.max(0, gypsumNeedKgHa - gypsumYear1KgHa), gypsumMaxPerYear) : 0

    // K fertiliser calculations
    const kDepthFactor = lookupDepthFactor(densityOperator, effectiveDepth)
    const kNeedKgHa = calcKNeed(kDiffMgKg, kDepthFactor)
    const kFertYear1KgHa = calcKAppliedYear1(kNeedKgHa, 500, template.potassium?.splitOver2Years ?? false)
    const kFertYear2KgHa = calcKAppliedYear2(kNeedKgHa, kFertYear1KgHa, 500, template.potassium?.splitOver2Years ?? false)

    // P fertiliser calculations
    const pIdeal = template.phosphorus?.idealMgKg ?? 30
    const pNeedKgHa = calcPNeed(row.pBrayI, pIdeal, depthFactor)
    const pFertYear1KgHa = calcPAppliedYear1(pNeedKgHa, 50, 500)
    const pFertYear2KgHa = calcPAppliedYear2(pNeedKgHa, pFertYear1KgHa, 500, false)

    // Elemental Sulphur calculations
    const phOperator = lookupPhOperator(row.cec)
    const elementalSulphurKgHa = calcElementalSulphurNeed(row.phH2O, phOperator)

    // Nutricast calculations (if enabled via template)
    const nutricastKIdeal = template.potassium?.idealMgKg ?? 150
    const nutricastNeedKgHa = calcNutricastNeedK(row.k, nutricastKIdeal, depthFactor)
    const nutricastYear1KgHa = calcNutricastAppliedYear1(nutricastNeedKgHa, 1000, false) // Disabled by default

    // Nutrient Inputs from amendments (mg/kg added)
    const caInputMgKg = calcCaInputFromLime(appliedYear1KgHa, limeType, depthFactor)
    const mgInputMgKg = calcMgInputFromLime(appliedYear1KgHa, limeType, depthFactor)
    const kInputMgKg = calcKInputFromFert(kFertYear1KgHa, depthFactor)
    const pInputMgKg = calcPInputFromFert(pFertYear1KgHa, depthFactor)

    // New calculated nutrient levels (mg/kg)
    const newCaMgKg = calcNewNutrientLevel(row.ca, caInputMgKg)
    const newMgMgKg = calcNewNutrientLevel(row.mg, mgInputMgKg)
    const newKMgKg = calcNewNutrientLevel(row.k, kInputMgKg)
    const newPMgKg = calcNewNutrientLevel(row.pBrayI, pInputMgKg)
    const newNaMgKg = row.na // Na doesn't change from lime/fertiliser

    // New CEC and ratios
    const newCec = calcNewCec(newCaMgKg, newMgMgKg, newKMgKg, newNaMgKg)
    const newCaPercent = calcNewSaturationPercent(newCaMgKg, 'Ca', newCec)
    const newMgPercent = calcNewSaturationPercent(newMgMgKg, 'Mg', newCec)
    const newKPercent = calcNewSaturationPercent(newKMgKg, 'K', newCec)
    const newNaPercent = calcNewSaturationPercent(newNaMgKg, 'Na', newCec)
    const newCaMgRatio = calcNewCaMgRatio(newCaPercent, newMgPercent)

    return {
        referenceNr: row.referenceNr,
        phKcl: row.phH2O,
        pBrayI: row.pBrayI,
        kMgKg: row.k,
        naMgKg: row.na,
        caMgKg: row.ca,
        mgMgKg: row.mg,
        exchH: row.exchH,
        sulfur: row.sulfur,
        density: row.density,
        kCmolc: mgKgToCmolc(row.k, 'K'),
        naCmolc: mgKgToCmolc(row.na, 'Na'),
        caCmolc: mgKgToCmolc(row.ca, 'Ca'),
        mgCmolc: mgKgToCmolc(row.mg, 'Mg'),
        pKgHa: mgKgToKgHa(row.pBrayI, row.density, effectiveDepth),
        kKgHa: mgKgToKgHa(row.k, row.density, effectiveDepth),
        naKgHa: mgKgToKgHa(row.na, row.density, effectiveDepth),
        caKgHa: mgKgToKgHa(row.ca, row.density, effectiveDepth),
        mgKgHa: mgKgToKgHa(row.mg, row.density, effectiveDepth),
        sValue: row.sValue,
        cec: row.cec,
        caMgRatio: ratio(caPercent, mgPercent),
        naKRatio: ratio(naPercent, kPercent),
        caMgKRatio: ratio(caPercent + mgPercent, kPercent),
        kPercent,
        naPercent,
        caPercent,
        mgPercent,
        // Operators
        cecOperator,
        densityOperator,
        // % Base saturation norms
        caPercentNorm: norms.ca,
        mgPercentNorm: norms.mg,
        caMgRatioNorm: norms.caMgRatio,
        kPercentNorm: norms.k,
        // Nutrient level at norms (mg/kg)
        caNormMgKg,
        mgNormMgKg,
        kNormMgKg,
        // Nutrient difference (actual - norm)
        caDiffMgKg,
        mgDiffMgKg,
        kDiffMgKg,
        // Lime calculations
        limeRule,
        depthFactor,
        limeType,
        caSupplyKgHa,
        mgSupplyKgHa,
        amendmentNeedKgHa,
        appliedYear1KgHa,
        appliedYear2KgHa,
        appliedYear3KgHa,
        // Gypsum calculations
        gypsumMeaningful,
        gypsumNeedKgHa,
        gypsumYear1KgHa,
        gypsumYear2KgHa,
        // K fertiliser calculations
        kNeedKgHa,
        kFertYear1KgHa,
        kFertYear2KgHa,
        // P fertiliser calculations
        pNeedKgHa,
        pFertYear1KgHa,
        pFertYear2KgHa,
        // Elemental Sulphur calculations
        phOperator,
        elementalSulphurKgHa,
        // Nutricast calculations
        nutricastNeedKgHa,
        nutricastYear1KgHa,
        // Nutrient Inputs (mg/kg added)
        caInputMgKg,
        mgInputMgKg,
        kInputMgKg,
        pInputMgKg,
        // New calculated nutrient levels (mg/kg)
        newCaMgKg,
        newMgMgKg,
        newKMgKg,
        newPMgKg,
        newNaMgKg,
        // New CEC and ratios
        newCec,
        newCaMgRatio,
        // Saturation before (% - same as existing percentages)
        satBeforeCa: caPercent,
        satBeforeMg: mgPercent,
        satBeforeK: kPercent,
        satBeforeNa: naPercent,
        // Saturation after (%)
        satAfterCa: newCaPercent,
        satAfterMg: newMgPercent,
        satAfterK: newKPercent,
        satAfterNa: newNaPercent
    }
}

/**
 * Calculate lab info for all rows
 */
export function calculateLabInfo(rows: LabInputRow[], template: TemplateData, depthCm?: number): LabInfoRow[] {
    return rows.map(row => calculateLabInfoRow(row, template, depthCm))
}

/**
 * Convert LimeProgramLabInputRow to LabInputRow format for calculations.
 * This handles the conversion from stored program data to the format needed by calculation functions.
 */
export function convertToLabInputRow(row: LimeProgramLabInputRow): LabInputRow {
    const caCmolc = row.ca / ELEMENT_SPECIFIC_GRAVITY.Ca
    const mgCmolc = row.mg / ELEMENT_SPECIFIC_GRAVITY.Mg
    const kCmolc = row.k / ELEMENT_SPECIFIC_GRAVITY.K
    const naCmolc = row.na / ELEMENT_SPECIFIC_GRAVITY.Na
    const calculatedCec = caCmolc + mgCmolc + kCmolc + naCmolc + row.exchH

    const cec = row.cec ?? calculatedCec

    const caPercent = row.caPercent ?? (cec > 0 ? (caCmolc / cec) * 100 : 0)
    const mgPercent = row.mgPercent ?? (cec > 0 ? (mgCmolc / cec) * 100 : 0)
    const kPercent = row.kPercent ?? (cec > 0 ? (kCmolc / cec) * 100 : 0)
    const naPercent = row.naPercent ?? (cec > 0 ? (naCmolc / cec) * 100 : 0)

    const caMgRatio = row.caMgRatio ?? (mgPercent > 0 ? caPercent / mgPercent : 0)
    const caMgKRatio = row.caMgKRatio ?? (kPercent > 0 ? (caPercent + mgPercent) / kPercent : 0)
    const mgKRatio = row.mgKRatio ?? (kPercent > 0 ? mgPercent / kPercent : 0)
    const naKRatio = row.naKRatio ?? (kPercent > 0 ? naPercent / kPercent : 0)

    const sValue = row.sValue ?? (caCmolc + mgCmolc + kCmolc + naCmolc)
    const acidSat = row.acidSat ?? (cec > 0 ? (row.exchH / cec) * 100 : 0)

    return {
        landRef: row.landRef,
        referenceNr: row.referenceNr,
        phH2O: row.phH2O,
        pBrayI: row.pBrayI,
        k: row.k,
        na: row.na,
        ca: row.ca,
        mg: row.mg,
        exchH: row.exchH,
        sulfur: row.sulfur,
        density: row.density,
        caPercent,
        mgPercent,
        kPercent,
        naPercent,
        acidSat,
        caMgRatio,
        caMgKRatio,
        mgKRatio,
        naKRatio,
        sValue,
        cec
    }
}

/**
 * Calculate recommendation rows for a land's lab inputs using template data.
 * This is a convenience function that combines convertToLabInputRow and calculateLabInfoRow.
 */
export function calculateLandRows(labInputs: LimeProgramLabInputs, templateData: TemplateData): LimePlayFieldRow[] {
    return labInputs.rows.map(row => {
        const labInput = convertToLabInputRow(row)
        return calculateLabInfoRow(labInput, templateData)
    })
}
